/**
 * One metric of one of an instance's own resources, over a bounded range — the
 * investigator's metric read (IM-209).
 *
 * ## A target, never a namespace
 *
 * The request names what kind of resource and its id, and the adapter builds
 * the namespace and dimensions from that. So a caller cannot ask for an
 * arbitrary CloudWatch series in the customer's account — only these metrics,
 * of these three kinds of thing — and the investigator checks the id against
 * the instance's own resources before it asks at all. Which resources those are
 * is decided by tag, as every other read of the instance is.
 *
 * Each metric is one the diagnostics role already reads for the checks
 * (`cloudwatch:GetMetricData`), so this asks for nothing new in anyone's account.
 */
export const METRIC_TARGET_KINDS = ['node', 'database', 'search-domain'];
/** The metrics each kind may be asked for. */
export const METRIC_TARGETS = {
    node: [
        'CPUUtilization',
        'CPUCreditBalance',
        'CPUCreditUsage',
        'CPUSurplusCreditBalance',
        'CPUSurplusCreditsCharged',
        'NetworkIn',
        'NetworkOut',
        'StatusCheckFailed',
    ],
    database: [
        'CPUUtilization',
        'FreeableMemory',
        'DatabaseConnections',
        'FreeStorageSpace',
        'ReadIOPS',
        'WriteIOPS',
        'ReadLatency',
        'WriteLatency',
        'ServerlessDatabaseCapacity',
        'ACUUtilization',
    ],
    'search-domain': [
        'CPUUtilization',
        'JVMMemoryPressure',
        'FreeStorageSpace',
        'ClusterStatus.red',
        'ClusterStatus.yellow',
        'SearchLatency',
        'IndexingLatency',
        'ThreadpoolSearchRejected',
        'ThreadpoolWriteRejected',
    ],
};
export const METRIC_STATISTICS = ['Average', 'Maximum', 'Minimum', 'Sum'];
/** A week: what "since the problem started" usually needs, and CloudWatch keeps hourly data far longer. */
export const MAX_METRIC_HOURS = 168;
/** At most this many points come back, so a long range is coarser rather than larger. */
export const MAX_METRIC_POINTS = 200;
/** The coarsest period that keeps a range within `MAX_METRIC_POINTS`: five minutes, an hour or a day. */
export const metricPeriodSeconds = (hours) => {
    for (const period of [300, 3_600, 86_400]) {
        if ((hours * 3_600) / period <= MAX_METRIC_POINTS)
            return period;
    }
    return 86_400;
};
/** Why a metric request cannot be asked, or nothing when it can. */
export const resourceMetricProblem = (request) => {
    const kind = request.target.kind;
    if (!METRIC_TARGET_KINDS.includes(kind)) {
        return `A metric target is one of ${METRIC_TARGET_KINDS.join(', ')}.`;
    }
    if (!METRIC_TARGETS[kind].includes(request.metric)) {
        return `A ${kind} metric is one of ${METRIC_TARGETS[kind].join(', ')}.`;
    }
    if (!METRIC_STATISTICS.includes(request.statistic)) {
        return `A statistic is one of ${METRIC_STATISTICS.join(', ')}.`;
    }
    if (!Number.isInteger(request.hours) || request.hours < 1 || request.hours > MAX_METRIC_HOURS) {
        return `A range is a whole number of hours from 1 to ${String(MAX_METRIC_HOURS)}.`;
    }
    return undefined;
};
//# sourceMappingURL=resource-metric.js.map